import { type LoaderFunctionArgs, redirect } from "react-router";
import { BASE_PATH } from "src/basepath";
import { getToken } from "src/business-hooks/useToken";
import { impersonateTenant } from "src/super-admin/SuperAdmin.utils";
import { tokenAtom } from "src/utils/auth/auth-atoms";
import { AppStore } from "src/utils/jotai";

/**
 * Loader that checks for a `tid` (tenant_id) param in the URL and impersonates that tenant.
 * An optional `uid` (user_id) param targets a specific user in that tenant
 * instead of the first tenant admin.
 * Only works for super admin users.
 * After impersonation, redirects to the same URL without the tid / uid params.
 */
export const tenantImpersonationLoader = async (
  args: LoaderFunctionArgs
): Promise<Response | null> => {
  // Must be logged in
  if (!getToken()) {
    return null;
  }

  const url = new URL(args.request.url);
  const tid = url.searchParams.get("tid");
  const uid = url.searchParams.get("uid");

  // No tid param, nothing to do
  if (!tid) {
    return null;
  }

  const token = AppStore.get(tokenAtom);

  // Only super admins can impersonate
  if (!token?.is_super_admin) {
    return null;
  }

  const getCleanPath = () => {
    url.searchParams.delete("tid");
    url.searchParams.delete("uid");
    return url.pathname.replace(BASE_PATH, "") + url.search + url.hash || "/";
  };

  // Already impersonating this tenant. Skip this shortcut when a specific user
  // was requested, since we may need to switch to a different user in the tenant.
  if (!uid && token.tenant_id === Number(tid)) {
    return redirect(getCleanPath());
  }

  // Parse an optional user id. Reject a malformed uid instead of silently
  // falling back to the tenant admin (that would impersonate the wrong user).
  let userId: number | undefined;
  if (uid !== null) {
    const parsed = Number(uid);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      console.error(`tenantImpersonationLoader: invalid uid "${uid}"`);
      return redirect(getCleanPath());
    }
    userId = parsed;
  }

  try {
    await impersonateTenant(Number(tid), userId);
    // Redirect to trigger a fresh load with new token
    return redirect(getCleanPath());
  } catch (error) {
    console.error("Failed to impersonate tenant:", error);
    // Remove tid param even on failure to prevent loops
    return redirect(getCleanPath());
  }
};
